import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import { TableRow, TableCell } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Chart from '../../../components/Chart';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import ChartButton from '../../../components/Button/ChartButton';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { ClearButton } from '../../../components/Button/iconbutton';
import { BloodUricAcid, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';


const BloodUricAcidForm: React.FC<{ patientId: string }> = ({ patientId }) => {
    const [bloodUricAcidRecords, setBloodUricAcidRecords] = useState<BloodUricAcid[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [editRecord, setEditRecord] = useState<BloodUricAcid | null>(null);
    const [showChart, setShowChart] = useState(false);
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'info' | 'warning' | 'error'
    >('success')
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [recordToDelete, setRecordToDelete] = useState<BloodUricAcid | null>(null)
    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };


    useEffect(() => {
        fetchBloodUricAcidRecords();
        setShowClearFilterButton(
            !!(searchTerm !== '' || (dateRange.startDate && dateRange.endDate))
        );
    }, [page, rowsPerPage, searchTerm, dateRange]);

    const fetchBloodUricAcidRecords = async (startDate: string = '', endDate: string = '') => {
        setBloodUricAcidRecords([]);
        setLoading(true);
        
        try {
            const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
                params: {
                    limit: rowsPerPage,
                    page: page + 1,
                    from: startDate || '',
                    to: endDate || '',
                    order_by: 'details->date',
                    dir: 2,
                    user_id: patientId,
                    slug: 'uric_acid',
                    searchkey: searchTerm || undefined,
                },
                headers: getAxiosConfig().headers,
            });

            const vitalsData = response.data.data.vitals;
            const total = response.data.data.pagination?.total;

            if (Array.isArray(vitalsData) && vitalsData.length > 0) {
                const formattedData: BloodUricAcid[] = vitalsData.map((vital) => ({
                    id: vital.id,
                    user_id: vital.user_id ?? 0, // Default if not present
                    consult_id: vital.consult_id ?? null,
                    peripheral_id: vital.peripheral_id ?? null,
                    slug: vital.slug ?? 'default_slug', // Default if not present
                    details: {
                        date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
                        time: vital.details.time,
                        uric_acid: vital.details.uric_acid,
                        uricFlag: vital.details.uricFlag,
                        uricFlagColor: vital.details.uricFlagColor,
                        range_code: vital.details.range_code,
                    },
                    freeze: vital.freeze ?? null,
                    document: vital.document ?? null,
                }));
                setBloodUricAcidRecords(formattedData);
                setTotalRecords(total ?? 0);
            } else {
                setBloodUricAcidRecords([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching blood uric acid records:', error);
            showSnackbar('Failed to fetch records.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ startDate: '', endDate: '' });
        setPage(0); // Reset to the first page
        setRowsPerPage(5); // Reset rows per page if needed
        fetchBloodUricAcidRecords(); // Fetch all records again
        setShowClearFilterButton(false); // Hide the clear button
    };

    const renderRow = (item: BloodUricAcid, index: number) => (
        <TableRow key={index}>
            <TableCell className="px-4 py-3 font-jost sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}">{page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{(item.details.date)}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}> {item.details.time}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.uric_acid} mg/dL</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                <span
                    style={{
                        backgroundColor: item.details.range_code,
                        padding: '4px 8px',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                    }}
                >
                    {item.details.uricFlag}
                </span>
            </TableCell>
        </TableRow>
    );

    const chartData = {
        labels: bloodUricAcidRecords.map(record => record.details.date),
        datasets: [
            {
                label: 'Blood Uric Acid (mg/dL)',
                data: bloodUricAcidRecords.map(record => record.details.uric_acid),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.dataset.label}: ${context.raw} mg/dL`,
                },
            },
        },
    };

    return (
        <div>
            <div className="flex items-end justify-between w-full gap-12">
                <div className="flex flex-grow space-x-2 gap-2">
                    <DateRangePicker
                        startDateId={'BloodUricAcid_Start_Date'}
                        endDateId={'BloodUricAcid_End_Date'}
                        onChange={(dates: { startDate: string; endDate: string }) => {
                            setDateRange(dates)
                        }}
                        value={dateRange}
                    />
                    <div className="flex items-center gap-2">
                        <IconButton
                            icon={<SearchIcon />}
                            onClick={() => fetchBloodUricAcidRecords(dateRange.startDate, dateRange.endDate)}
                        />
                        {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}</div>
                </div>
                <div className="flex space-x-4">
                    {bloodUricAcidRecords.length > 0 && (
                        <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
                    )}
                   </div>
            </div>

            {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
                            <TableComponent
                                headers={['S.No', 'Date', 'Time', 'Value (mg/dL)', 'Result']}
                                data={bloodUricAcidRecords}
                                loading={loading}
                                renderRow={renderRow} error={''} />
                            
                        </>
                  
            )}

            <TablePaginationComponent
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />

        </div >
    );
};

export default BloodUricAcidForm;
